<script>
import { computed, ref } from 'vue';
import { mapGetters } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useElementSize } from '@vueuse/core';
import { useUISettings } from 'dashboard/composables/useUISettings';
import {
  useConversationDisplayType,
  TABLE_LAYOUT_ROUTES,
} from 'dashboard/composables/useConversationDisplayType';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useAccount } from 'dashboard/composables/useAccount';
import { useConfig } from 'dashboard/composables/useConfig';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ChatList from '../../../components/ChatList.vue';
import ConversationBox from '../../../components/widgets/conversation/ConversationBox.vue';
import ConversationTable from 'dashboard/components/widgets/conversation/ConversationTable.vue';
import ConversationTableToolbar from 'dashboard/components/widgets/conversation/ConversationTableToolbar.vue';
import ConversationTableEmptyState from 'dashboard/components/widgets/conversation/ConversationTableEmptyState.vue';
import {
  presentTicket,
  rowMatchesSearch,
} from 'dashboard/helper/conversationRowPresenter';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import CmdBarConversationSnooze from 'dashboard/routes/dashboard/commands/CmdBarConversationSnooze.vue';
import LayoutSwitcherPill from 'dashboard/components-next/Conversation/LayoutSwitcherPill.vue';
import { emitter } from 'shared/helpers/mitt';
import { useSidepanelToggle } from 'dashboard/composables/useSidepanelToggle';
import { useTableScrollMemory } from 'dashboard/composables/useTableScrollMemory';
import { PANEL_RAIL_MIN_WIDTH } from 'dashboard/constants/breakpoints';
import ConversationSidebar from 'dashboard/components/widgets/conversation/ConversationSidebar.vue';
import SidepanelRail from 'dashboard/components-next/Conversation/SidepanelRail.vue';

export default {
  components: {
    ChatList,
    ConversationBox,
    ConversationTable,
    ConversationTableToolbar,
    ConversationTableEmptyState,
    CmdBarConversationSnooze,
    LayoutSwitcherPill,
    ConversationSidebar,
    SidepanelRail,
  },
  beforeRouteLeave(to, from, next) {
    // Clear selected state if navigating away from a conversation to a route without a conversationId to prevent stale data issues
    // and resolves timing issues during navigation with conversation view and other screens
    if (this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
    next(); // Continue with navigation
  },
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    conversationId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    teamId: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    // The Chats queues (For you, Unassigned) are this same screen opened on a
    // pinned assignee scope - see CHAT_QUEUES in the routes. The list controls
    // stay live: the scope is where a queue starts, not a cage.
    assigneeTypeScope: {
      type: String,
      default: '',
    },
    queueSlug: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { uiSettings, updateUISettings } = useUISettings();
    const { conversationDisplayType, isOnExpandedLayout, toggleTableLayout } =
      useConversationDisplayType();
    const { accountId } = useAccount();
    const { isEnterprise } = useConfig();
    const { getPlainText } = useMessageFormatter();
    useSidepanelToggle();

    const route = useRoute();
    const router = useRouter();

    const conversationViewRef = ref(null);
    const { width: viewWidth } = useElementSize(conversationViewRef);

    // The table layout is scoped to the main conversations queue; inbox and
    // other filtered routes keep the column layouts only.
    const isOnTableLayout = computed(
      () =>
        TABLE_LAYOUT_ROUTES.includes(route.name) &&
        conversationDisplayType.value === wootConstants.LAYOUT_TYPES.TABLE
    );

    // The open conversation replaces the table, so leaving it is a navigation
    // back to the queue - the same place the header's back arrow points at.
    const returnToTable = () => {
      router.push({
        name: 'home',
        params: { accountId: route.params.accountId },
      });
    };

    const isTableVisible = computed(
      () => isOnTableLayout.value && !props.conversationId
    );
    const tableScrollRef = useTableScrollMemory(isTableVisible);

    useKeyboardEvents({
      KeyL: () => {
        if (TABLE_LAYOUT_ROUTES.includes(route.name)) toggleTableLayout();
      },
      Escape: () => {
        if (isOnTableLayout.value && props.conversationId) returnToTable();
      },
    });

    return {
      uiSettings,
      updateUISettings,
      conversationDisplayType,
      isOnExpandedLayout,
      isOnTableLayout,
      isTableVisible,
      returnToTable,
      tableScrollRef,
      accountId,
      isEnterprise,
      getPlainText,
      conversationViewRef,
      viewWidth,
    };
  },
  data() {
    return {
      showSearchModal: false,
      // The table's search narrows the rows already loaded; the filters, which
      // the server answers, stay the toolbar's business.
      tableSearch: '',
      // The panel the rail reopens: the one that was last open, not always
      // Details.
      lastOpenPanelKey: 'details',
      // Once the Details panel has been opened it stays mounted and toggles
      // with v-show, so switching tabs never remounts it (no flicker, no
      // refetch, scroll position survives).
      detailsPanelWasOpened: false,
    };
  },
  computed: {
    ...mapGetters({
      chatList: 'getAllConversations',
      currentChat: 'getSelectedChat',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    showConversationList() {
      return this.isOnExpandedLayout ? !this.conversationId : true;
    },
    showMessageView() {
      return this.conversationId ? true : !this.isOnExpandedLayout;
    },
    tableRows() {
      return this.chatList.map(conversation =>
        presentTicket(conversation, this.getPlainText)
      );
    },
    visibleRows() {
      return this.tableRows.filter(row =>
        rowMatchesSearch(row, this.tableSearch)
      );
    },

    shouldShowSidebar() {
      if (!this.currentChat.id) {
        return false;
      }

      const { is_contact_sidebar_open: isContactSidebarOpen } = this.uiSettings;
      return isContactSidebarOpen;
    },

    // Which sidepanel is open, if any. Both flags are false once the panel is
    // collapsed, which is exactly when the rail stands in for it.
    openPanelKey() {
      const {
        is_contact_sidebar_open: isContactSidebarOpen,
        is_copilot_panel_open: isCopilotPanelOpen,
      } = this.uiSettings;
      if (isCopilotPanelOpen) return 'copilot';
      if (isContactSidebarOpen) return 'details';
      return null;
    },

    showPanelRail() {
      return (
        !this.openPanelKey &&
        !!this.currentChat.id &&
        this.viewWidth >= PANEL_RAIL_MIN_WIDTH
      );
    },

    // Mirrors SidepanelTabs: without Copilot the icon would be a dead end.
    panelRailTabs() {
      const tabs = [
        {
          key: 'details',
          icon: 'i-lucide-user-round',
          label: this.$t('CONVERSATION.SIDEBAR.DETAILS'),
        },
      ];
      const isCopilotAvailable =
        this.isEnterprise &&
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.CAPTAIN);
      if (isCopilotAvailable) {
        tabs.push({
          key: 'copilot',
          icon: 'i-lucide-sparkles',
          label: this.$t('CONVERSATION.SIDEBAR.COPILOT'),
        });
      }
      return tabs;
    },
  },
  watch: {
    conversationId() {
      this.fetchConversationIfUnavailable();
    },
    openPanelKey: {
      handler(key) {
        if (key) this.lastOpenPanelKey = key;
      },
      immediate: true,
    },
    shouldShowSidebar: {
      handler(isOpen) {
        if (isOpen) this.detailsPanelWasOpened = true;
      },
      immediate: true,
    },
  },

  created() {
    // Clear selected state early if no conversation is selected
    // This prevents child components from accessing stale data
    // and resolves timing issues during navigation
    // with conversation view and other screens
    if (!this.conversationId) {
      this.$store.dispatch('clearSelectedState');
    }
  },

  mounted() {
    this.$store.dispatch('agents/get');
    this.$store.dispatch('portals/index');
    this.initialize();
    this.$watch('$store.state.route', () => this.initialize());
    this.$watch('chatList.length', () => {
      this.setActiveChat();
    });
  },

  methods: {
    onConversationLoad() {
      this.fetchConversationIfUnavailable();
    },
    initialize() {
      this.$store.dispatch('setActiveInbox', this.inboxId);
      this.setActiveChat();
    },
    toggleConversationLayout() {
      const { LAYOUT_TYPES } = wootConstants;
      const {
        conversation_display_type:
          conversationDisplayType = LAYOUT_TYPES.CONDENSED,
      } = this.uiSettings;
      const newViewType =
        conversationDisplayType === LAYOUT_TYPES.CONDENSED
          ? LAYOUT_TYPES.EXPANDED
          : LAYOUT_TYPES.CONDENSED;
      this.updateUISettings({
        conversation_display_type: newViewType,
        previously_used_conversation_display_type: newViewType,
      });
    },
    fetchConversationIfUnavailable() {
      if (!this.conversationId) {
        return;
      }
      const chat = this.findConversation();
      if (!chat) {
        this.$store.dispatch('getConversation', this.conversationId);
      }
    },
    findConversation() {
      const conversationId = parseInt(this.conversationId, 10);
      const [chat] = this.chatList.filter(c => c.id === conversationId);
      return chat;
    },
    setActiveChat() {
      if (this.conversationId) {
        const selectedConversation = this.findConversation();
        // If conversation doesn't exist or selected conversation is same as the active
        // conversation, don't set active conversation.
        if (
          !selectedConversation ||
          selectedConversation.id === this.currentChat.id
        ) {
          return;
        }
        const { messageId } = this.$route.query;
        this.$store
          .dispatch('setActiveChat', {
            data: selectedConversation,
            after: messageId,
          })
          .then(() => {
            emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
          });
      } else {
        this.$store.dispatch('clearSelectedState');
      }
    },
    // The row opens the conversation full screen in place of the table; the
    // layout preference stays put, so the back arrow lands on the table again.
    openConversationFromTable(id) {
      this.$router.push(`/app/accounts/${this.accountId}/conversations/${id}`);
    },
    markConversationsResolved(ids) {
      ids.forEach(conversationId =>
        this.$store.dispatch('toggleStatus', {
          conversationId,
          status: wootConstants.STATUS_TYPE.RESOLVED,
        })
      );
    },
    // The rail drives the same UI settings the header button and the panel's
    // own tabs write, so the panel and the rail can never disagree.
    openPanel(key) {
      this.updateUISettings({
        is_contact_sidebar_open: key === 'details',
        is_copilot_panel_open: key === 'copilot',
      });
    },
    onSearch() {
      this.showSearchModal = true;
    },
    closeSearch() {
      this.showSearchModal = false;
    },
  },
};
</script>

<template>
  <section
    ref="conversationViewRef"
    class="flex w-full h-full min-w-0 bg-n-background lg:gap-2 lg:p-2"
  >
    <!-- ChatList owns the list request, so it stays mounted (and hidden) under
         the table layout too - the table reads the same store it fills. -->
    <ChatList
      :show-conversation-list="!isOnTableLayout && showConversationList"
      :conversation-inbox="inboxId"
      :label="label"
      :team-id="teamId"
      :conversation-type="conversationType"
      :folders-id="foldersId"
      :queue-slug="queueSlug"
      :assignee-type-override="assigneeTypeScope"
      :hide-scope-controls="Boolean(assigneeTypeScope)"
      :is-on-expanded-layout="isOnExpandedLayout"
      @conversation-load="onConversationLoad"
    />
    <!-- The table stays mounted under the open conversation, so returning to
         it keeps the selection and scroll offset it was left with. -->
    <div
      v-if="isOnTableLayout"
      v-show="isTableVisible"
      ref="tableScrollRef"
      class="flex-1 min-w-0 min-h-0 p-3 overflow-y-auto md:p-4"
    >
      <ConversationTableToolbar
        v-model:search="tableSearch"
        variant="conversations"
        :title="$t('CHAT_LIST.TAB_HEADING')"
      />
      <ConversationTable
        v-if="visibleRows.length"
        variant="conversations"
        :tickets="visibleRows"
        @open="openConversationFromTable"
        @mark-solved="markConversationsResolved"
      />
      <ConversationTableEmptyState
        v-else
        copy-key="CONVERSATION.TABLE"
        :show-clear-search="Boolean(tableSearch)"
        @clear-search="tableSearch = ''"
      />
    </div>
    <!-- The conversation is the screen once it is open: on the table layout it
         replaces the table, everywhere else it sits beside the list. -->
    <template v-if="!isTableVisible">
      <ConversationBox
        v-if="showMessageView"
        class="lg:overflow-hidden lg:rounded-xl lg:border lg:border-n-weak lg:shadow-sm [&_.conversation-panel]:bg-n-background [&_.reply-box]:!mx-3 [&_.reply-box]:!mb-3 [&_.reply-box]:!border-n-weak [&_.reply-box]:shadow-lg [&_.reply-box]:shadow-n-slate-12/5"
        :inbox-id="inboxId"
        :is-on-expanded-layout="isOnExpandedLayout"
        :show-back-button="isOnTableLayout"
      />
      <ConversationSidebar
        v-if="currentChat.id && detailsPanelWasOpened"
        v-show="shouldShowSidebar"
        class="motion-safe:animate-panel-slide-in"
        :current-chat="currentChat"
      />
      <SidepanelRail
        v-if="showPanelRail"
        :tabs="panelRailTabs"
        :active-key="lastOpenPanelKey"
        test-id="conversation-panel-rail"
        class="flex-shrink-0 w-[3.25rem] border rounded-xl border-n-weak bg-n-surface-1 shadow-sm"
        @select="openPanel"
      />
    </template>
    <CmdBarConversationSnooze />
    <LayoutSwitcherPill v-if="!conversationId" />
  </section>
</template>
